// All notifications with paging; "Mark all read" (SPEC §7.10).

import type { ApiResult } from "../../api/client";
import { fetchNotifications, markAllNotificationsRead } from "../../api/me";
import type { NotificationsPage } from "../../api/types";
import { t } from "../../i18n/i18n";
import { navigate, renderCurrentRoute, setRedraw, type RouteMatch } from "../../router";
import { buildItems } from "../../ui/bell";
import { createButton, createElement } from "../../ui/dom";
import { createCheckbox, type Field } from "../../ui/forms";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import { buildPager } from "../../ui/pager";

export async function renderNotificationsPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const pageNumber: number = Math.max(1, Number(route.query.get("page") ?? "1"));
  const unreadOnly: boolean = route.query.get("unread") === "1";
  const page: HTMLElement = startPage(container, t("notifications.title"));
  page.append(createLoading());
  const result: ApiResult<NotificationsPage> = await fetchNotifications(pageNumber, unreadOnly);
  if (result.ok === false) {
    showPageProblem(page, result.problem);
    return;
  }

  const draw = (): void => {
    drawPage(startPage(container, t("notifications.title")), result.data, unreadOnly);
  };
  setRedraw(container, draw);
  draw();
}

function drawPage(page: HTMLElement, data: NotificationsPage, unreadOnly: boolean): void {
  const unread: Field<HTMLInputElement> = createCheckbox(t("notifications.unreadOnly"), unreadOnly);
  unread.input.addEventListener("change", (): void => {
    navigate(unread.input.checked ? "/notifications?unread=1" : "/notifications");
  });
  const toolbar: HTMLDivElement = createElement("div", "toolbar");
  toolbar.append(unread.wrapper, createButton(t("notifications.markAllRead"), async (): Promise<void> => {
    await markAllNotificationsRead();
    await renderCurrentRoute();
  }));
  const pages: number = Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  const query: string = unreadOnly ? "&unread=1" : "";
  const pager: HTMLElement = buildPager(data.page, pages, (target: number): void => {
    navigate(`/notifications?page=${String(target)}${query}`);
  });
  const list: HTMLDivElement = createElement("div", "card");
  list.append(...buildItems(data.items));
  page.append(toolbar, list, pager);
}
